import { Link } from 'react-router-dom';
import { getAvailableRulesets, type RulesetConfig } from '@character-creator/data';
import { IgnusLogo } from '../components/ui/Icons';

export function RulesetPickerPage() {
  const rulesets = getAvailableRulesets();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Simple header for picker page */}
      <header className="border-b border-background-elevated bg-background-secondary/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-center justify-center h-16">
            <div className="flex items-center gap-3 text-text-primary">
              <D20Icon className="w-8 h-8 text-accent-gold" />
              <span className="font-display text-xl font-semibold">
                AIR RPG Character Creator
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-text-primary mb-4">
            Choose Your <span className="text-accent-gold">Ruleset</span>
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Select a ruleset to begin. Each ruleset provides different races,
            classes, and features for your character.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {rulesets.map((ruleset) => (
            <RulesetCard key={ruleset.id} ruleset={ruleset} />
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-background-elevated py-6 text-center text-text-muted text-sm">
        <p>AIR RPG Character Creator</p>
        <p className="mt-1">
          Dungeons & Dragons is a trademark of Wizards of the Coast
        </p>
      </footer>
    </div>
  );
}

function RulesetCard({ ruleset }: { ruleset: RulesetConfig }) {
  const isIgnus = ruleset.id === 'ignus';

  return (
    <Link
      to={`/${ruleset.id}`}
      className="card card-hover p-6 flex flex-col group"
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            isIgnus ? 'bg-orange-500/10 text-orange-500' : 'bg-accent-gold/10 text-accent-gold'
          }`}
        >
          {isIgnus ? <IgnusLogo className="w-7 h-7" /> : <BookIcon className="w-6 h-6" />}
        </div>
        {ruleset.startingLevel && ruleset.startingLevel > 1 && (
          <span className="text-xs px-2 py-1 rounded bg-background-elevated text-text-muted">
            Starts at Level {ruleset.startingLevel}
          </span>
        )}
      </div>

      <h2 className="font-display text-2xl font-semibold text-text-primary mb-2 group-hover:text-accent-gold transition-colors">
        {ruleset.name}
      </h2>

      <p className="text-text-secondary mb-4 flex-grow">
        {ruleset.description}
      </p>

      <div className="flex items-center gap-2 text-sm text-text-muted">
        <span>
          {isIgnus ? (
            <>Species, Lineages, Tiered Feats</>
          ) : (
            <>9 Races, 5 Classes, Full SRD Content</>
          )}
        </span>
        <ArrowIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}

function D20Icon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <polygon points="12,2 22,8.5 22,15.5 12,22 2,15.5 2,8.5" />
      <line x1="12" y1="2" x2="12" y2="22" />
      <line x1="2" y1="8.5" x2="22" y2="15.5" />
      <line x1="22" y1="8.5" x2="2" y2="15.5" />
    </svg>
  );
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}
